// Shared vertical layout for the winner scene AND the shuffle reel.
//
// Both reserve the SAME top (title) and bottom (panel) regions and let
// the card live in the flexible middle, so:
//   • the card sits at the EXACT same vertical position in the winner and
//     in the reel — no vertical jump on the reel→winner crossfade;
//   • the card never moves when the bottom panel swaps offer↔resolved
//     (the bottom region's height is reserved, so it can't reflow the
//     middle);
//   • the card still shrinks on short screens (it's the flex `grow` item
//     with `min-h-0`), so mobile layouts don't overflow.
//
// Inline CSS lengths (not Tailwind) so the value is shared from one place
// and the JIT scanner isn't involved.
//
// Both reserves are height-aware: they reach their full 260 / 216px from
// ~1000 / ~850px tall, and on SHORT viewports (a wide-but-low iframe,
// e.g. a desktop embed at ~500px tall) the `svh` term takes over and they
// shrink, so the fixed chrome doesn't eat the whole height and leave a
// postage-stamp card. The title font (WinnerContent's useFitText cap, see
// TITLE_MAX_SVH_FRACTION) and the FMV number (FmvOnly / BuybackReceivedLine)
// shrink on the same svh curve, so each reserve keeps covering its content
// and the reel↔winner alignment holds at every height.

// Top is a MIN-HEIGHT band: both the reel's empty spacer and the winner's
// title header reserve at least this much, so for short titles the card
// lands at the exact same height in both (no reel→winner jump). The title
// container may still GROW past it and the plain card shrinks proportionally
// via its `maxHeight: '100%'` + aspect-ratio — but the opening SCENE
// (pack-opener-js, OpenerReadyStep) has no such live compensation: it lays
// the card's resting place out from this band once, at boot, before the
// pulled card's title is even known (mystery-pack reveal), and the library
// locks the scene's geometry from the moment the player commits the cut
// until the ceremony's `reset()` — a correction reported after that (e.g.
// once the real title mounts) is silently deferred forever, never applied.
// So this MUST cover the worst case up front, not the common case: sized for
// the full `TITLE_MAX_LINES` (3, not 2 — the original 2-line sizing is what
// let a genuine 3-line title overlap the card, see the bug this comment
// replaces) at the svh-capped max font, plus a flat 16px of breathing room
// above the card so even a maxed-out title doesn't sit flush against it.
// 88px = 72px header padding (pt-16/pb-2, clears the top-corner controls —
// BalancePill / Exit: top-4 + h-9 = 52px, then a 12px gap) + that 16px
// buffer; 17.2svh = 3 lines at the svh-capped max font
// (3 × (52/1000)svh × 1.1 line-height, rounded up) — mirrors
// TITLE_MAX_SVH_FRACTION in WinnerContent (full size from 1000px tall, so
// phones get a smaller title and a bigger card). The reel reserves the same
// band, so changing this keeps the reel→winner handoff jump-free.
export const STAGE_TOP_RESERVE = 'min(260px, calc(88px + 17.2svh))';

// Sized to cover the offer panel (FMV block + gaps + CTA + shipping link
// + pb-6) at any height: ~158px of fixed rows (22px label, 2 × 12px gaps,
// the static 64px CTA, the link's 24px row, 24px pb-6) plus the svh-scaled
// FMV number (5.2svh × 1.27 line-height ≈ 6.6svh, see FmvOnly), with 2px to
// spare. Reaches the full 216px at ≥ ~850px tall.
export const STAGE_BOTTOM_RESERVE = 'min(216px, calc(160px + 6.6svh))';

// Sleek (partner mockup) moves the winner title BELOW the card, next to the
// FMV and the Vault / Sell row. The top band clears the corner controls plus
// the mockup's gap above the card (130px on its 874px frame, shrinking on
// short screens), and the bottom band grows by the title's two lines. Every
// consumer — the winner, the reel and the opener scene — must read the pair
// from `stageReserves`, so the card still lands at one position across all.
const SLEEK_STAGE_TOP_RESERVE = 'min(130px, calc(88px + 5svh))';
const SLEEK_STAGE_BOTTOM_RESERVE = 'min(300px, calc(200px + 10svh))';

export interface StageReserves {
  top: string;
  bottom: string;
}

export function stageReserves(isSleek: boolean): StageReserves {
  return isSleek
    ? { top: SLEEK_STAGE_TOP_RESERVE, bottom: SLEEK_STAGE_BOTTOM_RESERVE }
    : { top: STAGE_TOP_RESERVE, bottom: STAGE_BOTTOM_RESERVE };
}

// The card box's height inside its 320:500 frame. The other templates keep
// the bottom 16% for the pedestal; sleek has none, so its card fills the
// frame (the mockup's 355px card on an 874px screen). Winner, reel tile and
// opener probe all read this, keeping the card's landing spot identical.
const CARD_HEIGHT = '84%';
const SLEEK_CARD_HEIGHT = '100%';

export function cardHeight(isSleek: boolean): string {
  return isSleek ? SLEEK_CARD_HEIGHT : CARD_HEIGHT;
}

// The 320:500 frame the winner's card stands in, centred in the stage's
// flexible middle. Sleek's Vault card page lays its card out in the same
// frame, so the two screens put the card at one spot.
export const CARD_FRAME_STYLE = {
  aspectRatio: '320 / 500',
  height: 'min(56vh, 540px, 92vw * 500 / 320)',
  maxHeight: '100%',
} as const;

// The same frame on a stage laid out in a box of its own (a page's pane, not
// the screen): the box's lengths in place of the viewport's, so the card
// follows the box. The box has to be a size container.
export const CONTAINED_CARD_FRAME_HEIGHT = 'min(56cqh, 540px, 92cqw * 500 / 320)';
